import React, { useState, useEffect, useRef } from "react";
import { Pannellum } from "pannellum-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { useViewerState } from "./useViewerState";
import { toast } from "./ui/use-toast";
import { fetchWithRetry } from "../utils/tourUtils";
import { API_BASE_URL } from "../config";

// Generate UUID function
const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const MeasurementViewer = ({
  initialMeasurements = [],
  onMeasurementComplete,
}) => {
  const [measureMode, setMeasureMode] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [selectedNpyFile, setSelectedNpyFile] = useState(null);
  const [uploadingNpy, setUploadingNpy] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [measurements, setMeasurements] = useState(initialMeasurements);
  const [depthMap, setDepthMap] = useState(null);
  const [isInferencing, setIsInferencing] = useState(false);
  const [currentS3Key, setCurrentS3Key] = useState(null); // Store the S3 key for measurements
  const pannellumRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const inferenceImageInputRef = useRef(null);
  const activeHotspots = useRef(new Set());

  const {
    viewerState: { yaw, pitch, hfov },
    handleMouseDown,
    handleMouseUp,
  } = useViewerState({}, null);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      const imageUrl = URL.createObjectURL(file);
      setSelectedImage(imageUrl);
    } else {
      toast({
        title: "Invalid file",
        description: "Please select a valid image file (JPEG or PNG)",
        duration: 3000,
      });
    }
  };

  const handleInferenceImageSelect = async (event) => {
    const file = event.target.files[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      // Reset state
      setSelectedPoints([]);
      setMeasurements([]);
      clearHotspots();

      // Set the display image
      const imageUrl = URL.createObjectURL(file);
      setSelectedImage(imageUrl);

      setIsInferencing(true);
      try {
        // Generate UUID for this file
        const fileId = generateUUID();
        console.log("Generated file ID:", fileId);

        // Convert image to base64
        const reader = new FileReader();
        const base64Promise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
        });
        reader.readAsDataURL(file);
        const base64Image = await base64Promise;

        // Strip the data URL prefix to get just the base64 data
        // const base64Data = base64Image.split(',')[1];
        const base64Data = base64Image;
        console.log("Base64 image data length:", base64Data.length);
        console.log(
          "Base64 image data starts with:",
          base64Data.substring(0, 50),
        );

        // Send to Lambda for inference with UUID
        const response = await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          retries: 1,
          data: {
            function_name: "PROCESS_DEPTH_INFERENCE",
            payload: {
              image: base64Data, // Send the clean base64 data
              file_id: fileId, // Send the UUID
            },
          },
        });

        if (!response.depth_map_npy || !response.s3_key) {
          throw new Error("Failed to process image with Lambda");
        }

        // Store the S3 key for later measurements
        setCurrentS3Key(response.s3_key);
        console.log("Received S3 key:", response.s3_key);

        // Convert base64 to Blob and create File (for backward compatibility)
        const binaryString = window.atob(response.depth_map_npy);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: "application/octet-stream" });
        const npyFile = new File([blob], "depth_map.npy", {
          type: "application/octet-stream",
        });

        setSelectedNpyFile(npyFile);

        // Also set the depth map visualization if needed
        setDepthMap(response.depth_map_png);

        toast({
          title: "Success",
          description: "Depth map generated successfully",
          duration: 3000,
        });
      } catch (error) {
        console.error("Lambda inference error:", error);
        toast({
          title: "Error",
          description: error.message,
          duration: 3000,
        });
      } finally {
        setIsInferencing(false);
      }
    } else {
      toast({
        title: "Invalid file",
        description: "Please select a valid image file (JPEG or PNG)",
        duration: 3000,
      });
    }
  };

  const handleNpyFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith(".npy")) {
      setSelectedNpyFile(file); // Store the file object for upload
      // Start upload in background
      uploadNpyFile(file);
    } else {
      toast({
        title: "Invalid file",
        description: "Please select a .npy file",
        duration: 3000,
      });
    }
  };

  const uploadNpyFile = async (file) => {
    if (!file) return;

    setUploadingNpy(true);
    try {
      // Generate UUID for this file
      const fileId = generateUUID();
      console.log("Generated file ID for upload:", fileId);

      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const base64Data = await base64Promise;

      // Strip the data URL prefix to get just the base64 data
      const cleanBase64Data = base64Data.split(",")[1];
      console.log("NPY base64 data length:", cleanBase64Data.length);

      // Upload the file using fetchWithRetry with UUID
      const uploadResult = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        retries: 1,
        data: {
          function_name: "PROCESS_DEPTH_FILE",
          payload: {
            depth_file: cleanBase64Data, // Send the clean base64 data
            filename: file.name,
            file_id: fileId, // Send the UUID
          },
        },
      });

      if (!uploadResult.success || !uploadResult.s3_key) {
        throw new Error("Failed to upload NPY file");
      }

      // Store the S3 key for later measurements
      setCurrentS3Key(uploadResult.s3_key);
      console.log("Received S3 key from upload:", uploadResult.s3_key);

      toast({
        title: "Success",
        description: "NPY file uploaded successfully",
        duration: 3000,
      });
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Error",
        description: error.message,
        duration: 3000,
      });
    } finally {
      setUploadingNpy(false);
    }
  };

  // Function to add a point hotspot
  const addPointHotspot = (pitch, yaw, id, text) => {
    const viewer = pannellumRef.current?.getViewer();
    if (!viewer) return;

    viewer.addHotSpot({
      pitch: pitch,
      yaw: yaw,
      type: "info",
      text: text,
      id: id,
      cssClass: "custom-hotspot",
      tooltip: `Pitch: ${pitch.toFixed(2)}°, Yaw: ${yaw.toFixed(2)}°`,
    });
    activeHotspots.current.add(id);
  };

  // Function to add a distance hotspot
  const addDistanceHotspot = (pitch1, yaw1, pitch2, yaw2, distance) => {
    const viewer = pannellumRef.current?.getViewer();
    if (!viewer) return;

    // Calculate midpoint
    const midPitch = (pitch1 + pitch2) / 2;
    const midYaw = (yaw1 + yaw2) / 2;

    viewer.addHotSpot({
      pitch: midPitch,
      yaw: midYaw,
      type: "info",
      text: `${distance} cm`,
      id: "distance-line",
      cssClass: "distance-hotspot",
      tooltip: "Distance between points",
    });
    activeHotspots.current.add("distance-line");
  };

  // Function to clear all hotspots
  const clearHotspots = () => {
    const viewer = pannellumRef.current?.getViewer();
    if (!viewer) return;

    // Remove each tracked hotspot
    activeHotspots.current.forEach((id) => {
      try {
        viewer.removeHotSpot(id);
      } catch (error) {
        console.warn(`Failed to remove hotspot ${id}:`, error);
      }
    });
    activeHotspots.current.clear();
  };

  useEffect(() => {
    if (!pannellumRef.current || !measureMode || !currentS3Key) return;

    const viewer = pannellumRef.current.getViewer();
    if (!viewer) return;

    const handlePointSelection = async (event) => {
      // Prevent default context menu on right click
      if (event.type === "contextmenu") {
        event.preventDefault();
      }

      const coords = viewer.mouseEventToCoords(event);
      const pitch = coords[0];
      const yaw = coords[1];

      setIsProcessing(true);
      try {
        debugger;
        // Query depth data from lambda using S3 key
        const depthData = await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          retries: 1,
          data: {
            function_name: "GET_DEPTH_COORDINATES",
            payload: {
              npy_s3_key: currentS3Key, // Use the S3 key instead of filename
              pitch,
              yaw,
            },
          },
        });

        if (!depthData.success) {
          throw new Error("Failed to get depth data");
        }

        const { x, y, depth } = depthData;
        const newPoint = { x, y, depth, pitch, yaw };

        if (selectedPoints.length === 0) {
          // First point
          setSelectedPoints([newPoint]);
          addPointHotspot(pitch, yaw, "point-1", "Point 1");

          toast({
            title: "First point selected",
            description: `Yaw: ${yaw.toFixed(2)}°, Pitch: ${pitch.toFixed(2)}°`,
            duration: 3000,
          });
        } else {
          // Second point
          const point1 = selectedPoints[0];
          const point2 = newPoint;

          // Add second point hotspot
          addPointHotspot(pitch, yaw, "point-2", "Point 2");

          // Convert to 3D point on sphere
          const toRad = (deg) => (deg * Math.PI) / 180;
          const sphToXYZ = (pt) => {
            const theta = toRad(pt.yaw);
            const phi = toRad(90 - pt.pitch);
            return [
              pt.depth * Math.sin(phi) * Math.sin(theta),
              pt.depth * Math.cos(phi),
              pt.depth * Math.sin(phi) * Math.cos(theta),
            ];
          };

          const a = sphToXYZ(point1);
          const b = sphToXYZ(point2);
          const distance = Math.sqrt(
            (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2,
          );

          const measurement = {
            id: Date.now(),
            point1: {
              pitch: point1.pitch,
              yaw: point1.yaw,
              depth: point1.depth,
              x: point1.x,
              y: point1.y,
            },
            point2: {
              pitch: point2.pitch,
              yaw: point2.yaw,
              depth: point2.depth,
              x: point2.x,
              y: point2.y,
            },
            distance: parseFloat((distance * 100).toFixed(1)),
          };

          // Add distance hotspot
          addDistanceHotspot(
            point1.pitch,
            point1.yaw,
            point2.pitch,
            point2.yaw,
            measurement.distance,
          );

          setMeasurements((prev) => [...prev, measurement]);

          if (onMeasurementComplete) {
            onMeasurementComplete(measurement);
          }

          toast({
            title: "Measurement Complete",
            description: `Distance: ${(distance * 100).toFixed(1)} cm`,
            duration: 4000,
          });

          // Reset for next measurement
          setSelectedPoints([]);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: error.message,
          duration: 3000,
        });
      } finally {
        setIsProcessing(false);
      }
    };

    // Handle right click
    const handleContextMenu = (event) => {
      if (measureMode) {
        handlePointSelection(event);
      }
    };

    // Handle space key
    const handleKeyDown = (event) => {
      if (measureMode && event.code === "Space") {
        // Get current view coordinates
        const pitch = viewer.getPitch();
        const yaw = viewer.getYaw();

        // Create a synthetic event with the current coordinates
        const syntheticEvent = {
          type: "keyboard",
          preventDefault: () => {},
          target: viewer.getContainer(),
        };

        // Add coordinates to the viewer for the handler to use
        viewer.mouseEventToCoords = () => [pitch, yaw];

        handlePointSelection(syntheticEvent);
      }
    };

    // Add event listeners
    viewer.getContainer().addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      // Clean up event listeners
      viewer
        .getContainer()
        .removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [measureMode, selectedPoints, onMeasurementComplete, currentS3Key]);

  return (
    <div className="flex h-screen">
      {/* Left Column - Controls and Info */}
      <div className="w-80 bg-white border-r border-gray-200 p-4 overflow-y-auto">
        <div className="space-y-6">
          {/* Display Image Selection */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Display 360° Image</h3>
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleImageSelect}
              ref={imageInputRef}
              className="hidden"
            />
            <Button
              onClick={() => imageInputRef.current?.click()}
              className="w-full"
              variant="outline"
            >
              🖼️ Select Display Image
            </Button>
            {selectedImage && (
              <p className="text-sm text-gray-500 truncate">Image selected</p>
            )}
          </div>

          {/* Inference Image Selection */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Generate Depth Map</h3>
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleInferenceImageSelect}
              ref={inferenceImageInputRef}
              className="hidden"
            />
            <Button
              onClick={() => inferenceImageInputRef.current?.click()}
              className="w-full"
              variant="outline"
              disabled={isInferencing}
            >
              {isInferencing ? "Processing..." : "🎯 Generate Depth Map"}
            </Button>
            {selectedNpyFile && (
              <p className="text-sm text-gray-500 truncate">
                Depth map generated
              </p>
            )}
            {currentS3Key && (
              <p className="text-xs text-blue-500 truncate">
                S3: {currentS3Key}
              </p>
            )}
          </div>

          {/* Depth Map Display */}
          {depthMap && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Depth Map Preview</h3>
              <div className="border rounded p-2">
                <img
                  src={`data:image/png;base64,${depthMap}`}
                  alt="Depth Map"
                  className="w-full h-auto"
                />
              </div>
            </div>
          )}

          <div className="h-px bg-gray-200" />

          {/* NPY File Controls */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Depth Data</h3>
            <input
              type="file"
              accept=".npy"
              onChange={handleNpyFileSelect}
              ref={fileInputRef}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="w-full"
              variant="outline"
            >
              📁 Select NPY File
            </Button>
            {selectedNpyFile && (
              <>
                <p className="text-sm text-gray-500 truncate">
                  {selectedNpyFile.name}
                </p>
                {uploadingNpy && (
                  <div className="text-sm text-gray-500">Uploading...</div>
                )}
              </>
            )}
          </div>

          <div className="h-px bg-gray-200" />

          {/* Add instructions for point selection */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">How to Measure</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>1. Toggle Measure Mode</p>
              <p>2. Right-click or press Space to select points</p>
              <p>3. First point: Right-click or Space</p>
              <p>4. Second point: Right-click or Space</p>
            </div>
          </div>

          {/* Measurements List */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Measurements</h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {measurements.map((measurement) => (
                <div
                  key={measurement.id}
                  className="text-sm border rounded p-2 bg-gray-50"
                >
                  <div className="font-medium text-green-600 mb-1">
                    📏 {measurement.distance} cm
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                      <div className="font-medium">Point 1:</div>
                      <div>Pitch: {measurement.point1.pitch.toFixed(2)}°</div>
                      <div>Yaw: {measurement.point1.yaw.toFixed(2)}°</div>
                      <div>Depth: {measurement.point1.depth.toFixed(2)}m</div>
                    </div>
                    <div>
                      <div className="font-medium">Point 2:</div>
                      <div>Pitch: {measurement.point2.pitch.toFixed(2)}°</div>
                      <div>Yaw: {measurement.point2.yaw.toFixed(2)}°</div>
                      <div>Depth: {measurement.point2.depth.toFixed(2)}m</div>
                    </div>
                  </div>
                </div>
              ))}
              {measurements.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-4">
                  No measurements yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Viewer and Measurement Points */}
      <div className="flex-1 flex flex-col">
        {/* Viewer */}
        <div className="flex-1 relative">
          {selectedImage ? (
            <>
              <Pannellum
                hotspotDebug={true}
                ref={pannellumRef}
                width="100%"
                height="100%"
                image={selectedImage}
                pitch={pitch}
                yaw={yaw}
                hfov={hfov}
                autoLoad
                onMousedown={handleMouseDown}
                onMouseup={handleMouseUp}
                onError={(error) => {
                  console.error("Error loading panorama:", error);
                }}
              />
              <style>
                {`
                  .custom-hotspot {
                    background-color: #ff4444;
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    border: 1px solid white;
                    box-shadow: 0 0 5px rgba(0,0,0,0.5);
                  }
                  .distance-hotspot {
                    background-color: rgba(0, 0, 0, 0.7);
                    color: white;
                    padding: 2px 6px;
                    border-radius: 3px;
                    font-size: 10px;
                    white-space: nowrap;
                  }
                  .pnlm-hotspot-base {
                    transition: all 0.3s ease;
                  }
                  .pnlm-hotspot-base:hover {
                    transform: scale(1.1);
                  }
                `}
              </style>
              {isProcessing && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="text-white text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
                    <p className="text-lg font-medium">
                      {selectedPoints.length === 0
                        ? "Processing first point..."
                        : "Calculating distance..."}
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <p className="text-gray-500">Select a 360° image to begin</p>
            </div>
          )}
        </div>

        {/* Measurement Controls */}
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Measurement Mode</h3>
            <Button
              onClick={() => {
                setMeasureMode(!measureMode);
                setSelectedPoints([]);
              }}
              className={`${measureMode ? "bg-yellow-500 hover:bg-yellow-600" : ""}`}
            >
              🧭{" "}
              {measureMode
                ? "Measuring (click to disable)"
                : "Toggle Measure Mode"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeasurementViewer;
